import { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged, type User as FirebaseUser } from "firebase/auth";
import { isAxiosError } from "axios";
import { firebaseAuth } from "@/lib/firebase";
import { apiClient } from "@/api/client";

export interface UserProfile {
  id: string;
  email: string;
  name: string;
  role: string;
  specialityId: string | null;
  hospital?: {
    name: string;
    nameRu?: string | null;
    nameKz?: string | null;
  };
}

interface AuthContextValue {
  firebaseUser: FirebaseUser | null;
  profile: UserProfile | null;
  isPending: boolean;
  authError: string | null;
}

const AuthContext = createContext<AuthContextValue>({
  firebaseUser: null,
  profile: null,
  isPending: true,
  authError: null,
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isPending, setIsPending] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    return onAuthStateChanged(firebaseAuth, async (fbUser) => {
      setFirebaseUser(fbUser);
      setAuthError(null);
      if (fbUser) {
        try {
          const token = await fbUser.getIdToken();
          const response = await apiClient.get<{
            success: boolean;
            data: UserProfile;
          }>("/api/v1/auth/me", {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (response.data.success) {
            setProfile(response.data.data);
          } else {
            setProfile(null);
          }
        } catch (error) {
          console.error("Auth: Failed to fetch user profile", error);
          setProfile(null);

          let message = "Unable to connect to the server. Please try again.";
          if (isAxiosError(error)) {
            const status = error.response?.status;
            if (status === 401 || status === 403) {
              message = "Your session has expired. Please sign in again.";
              await firebaseAuth.signOut();
            } else if (status === 404) {
              message = "Your account was not found. Please contact an administrator.";
              await firebaseAuth.signOut();
            } else if (status != null) {
              message = `Server error (${status}). Please try again.`;
            }
          }
          setAuthError(message);
        }
      } else {
        setProfile(null);
      }
      setIsPending(false);
    });
  }, []);

  if (isPending) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-primary" />
      </div>
    );
  }

  if (authError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="max-w-sm w-full space-y-4 p-6 text-center">
          <p className="text-destructive font-medium">{authError}</p>
          <button
            className="text-sm text-muted-foreground underline"
            onClick={() => firebaseAuth.signOut()}
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ firebaseUser, profile, isPending, authError }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
